import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { Optional } from '@zen/utils/typescript';

import { toGroupByResult } from '../helpers';
import type { GroupByResult, GroupByValue } from '../types';

export interface GroupBucket<T, TRow extends { original?: T } = { original?: T }> {
  groupKey: string;
  groupLabel: ReactNode;
  isCollapsed: boolean;
  items: T[];
  rows: TRow[];
}

type UngroupedBucket<T, TRow extends { original?: T }> = Omit<GroupBucket<T, TRow>, 'isCollapsed'>;

interface UseTableGroupingParams<T, TRow extends { original?: T }> {
  groupBy?: (record: T) => GroupByValue;
  initialCollapsedGroups?: string[];
  rows: TRow[];
}

interface UseTableGroupingReturn<T, TRow extends { original?: T }> {
  collapsedGroups: Record<string, boolean>;
  groupedBuckets: GroupBucket<T, TRow>[];
  isGrouped: boolean;
  toggleGroupCollapse: (groupKey: string) => void;
}

/**
 * Buckets table rows by the result of `groupBy`, preserving first-seen group order.
 * Pass a stable (memoised) `groupBy` reference, otherwise the grouping is recomputed on every render.
 */
export const useTableGrouping = <T, TRow extends { original?: T }>({
  groupBy,
  initialCollapsedGroups,
  rows
}: UseTableGroupingParams<T, TRow>): UseTableGroupingReturn<T, TRow> => {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>((): Record<string, boolean> => {
    if (!initialCollapsedGroups) return {};

    return Object.fromEntries(initialCollapsedGroups.map((key: string): [string, boolean] => [key, true]));
  });

  const toggleGroupCollapse = useCallback((groupKey: string): void => {
    setCollapsedGroups(
      (previous: Record<string, boolean>): Record<string, boolean> => ({
        ...previous,
        [groupKey]: !previous[groupKey]
      })
    );
  }, []);

  // Kept separate from collapse state so toggling a group doesn't regroup every row
  const buckets: UngroupedBucket<T, TRow>[] = useMemo((): UngroupedBucket<T, TRow>[] => {
    if (!groupBy) return [];

    const bucketsMap: Map<string, UngroupedBucket<T, TRow>> = new Map<string, UngroupedBucket<T, TRow>>();

    rows.forEach((row: TRow): void => {
      const item: T | undefined = row.original;

      if (!item) return;

      const groupValue: GroupByValue = groupBy(item);

      if (!groupValue) return;

      const { key, label }: GroupByResult = toGroupByResult(groupValue);
      const existingBucket: Optional<UngroupedBucket<T, TRow>> = bucketsMap.get(key);

      if (existingBucket) {
        existingBucket.rows.push(row);
        existingBucket.items.push(item);

        return;
      }

      bucketsMap.set(key, { groupKey: key, groupLabel: label, items: [item], rows: [row] });
    });

    return Array.from(bucketsMap.values());
  }, [groupBy, rows]);

  const groupedBuckets: GroupBucket<T, TRow>[] = useMemo(
    (): GroupBucket<T, TRow>[] =>
      buckets.map(
        (bucket: UngroupedBucket<T, TRow>): GroupBucket<T, TRow> => ({
          ...bucket,
          isCollapsed: Boolean(collapsedGroups[bucket.groupKey])
        })
      ),
    [buckets, collapsedGroups]
  );

  return {
    collapsedGroups,
    groupedBuckets,
    isGrouped: Boolean(groupBy),
    toggleGroupCollapse
  };
};
